import React, { useState, useEffect, useCallback, useRef } from "react";

// -------------------------------------------------------------
// IMPORTANT: audio loaded from public/ directory for reliability
// -------------------------------------------------------------
const SOUNDTRACK_SRC = "/soundtrack.mp3";

const CANVAS_WIDTH = 1000;
const CANVAS_HEIGHT = 640;
const BAY_WIDTH = 80;
const BAY_HEIGHT = 60;

// ——————————————————————— Helpers ———————————————————————
function distance(a, b) {
  return Math.hypot(a.x - b.x, a.y - b.y);
}

function calculatePath(start, end) {
  const laneY = 300;

  return [
    { ...start },
    { x: start.x, y: laneY },
    { x: end.x, y: laneY },
    { x: end.x, y: end.y },
  ];
}

// ————————————————————— Canvas Renderer —————————————————————
function TrafficCanvas({ cars, bays, width, height }) {
  const canvasRef = useRef(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    if (!ctx) return;

    // Background
    const bgGrad = ctx.createLinearGradient(0, 0, 0, height);
    bgGrad.addColorStop(0, "hsl(215, 28%, 8%)");
    bgGrad.addColorStop(1, "hsl(215, 28%, 10%)");
    ctx.fillStyle = bgGrad;
    ctx.fillRect(0, 0, width, height);

    // Inner lot
    const lotGradient = ctx.createRadialGradient(
      width / 2,
      height / 2,
      0,
      width / 2,
      height / 2,
      width
    );
    lotGradient.addColorStop(0, "hsl(215, 20%, 16%)");
    lotGradient.addColorStop(1, "hsl(215, 20%, 14%)");
    ctx.fillStyle = lotGradient;
    ctx.fillRect(50, 50, width - 100, height - 100);

    // Grid lines
    ctx.strokeStyle = "hsl(215, 20%, 18%)";
    ctx.lineWidth = 1;
    for (let i = 100; i < width; i += 50) {
      ctx.beginPath();
      ctx.moveTo(i, 50);
      ctx.lineTo(i, height - 50);
      ctx.stroke();
    }
    for (let i = 100; i < height; i += 50) {
      ctx.beginPath();
      ctx.moveTo(50, i);
      ctx.lineTo(width - 50, i);
      ctx.stroke();
    }

    // Lane dashed
    ctx.strokeStyle = "hsl(215, 20%, 22%)";
    ctx.setLineDash([15, 10]);
    ctx.lineWidth = 3;
    ctx.beginPath();
    ctx.moveTo(50, 300);
    ctx.lineTo(width - 50, 300);
    ctx.stroke();
    ctx.setLineDash([]);

    // Entry marker
    ctx.fillStyle = "hsl(210, 100%, 65%)";
    ctx.shadowColor = "hsl(210, 100%, 65%)";
    ctx.shadowBlur = 14;
    ctx.fillRect(width / 2 - 30, 45, 60, 8);
    ctx.shadowBlur = 0;

    ctx.font = "bold 10px monospace";
    ctx.textAlign = "center";
    ctx.fillText("ENTRY / EXIT", width / 2, 35);

    // — Bays
    bays.forEach((bay) => {
      const { x, y } = bay.position;

      if (bay.isPremium) {
        const grad = ctx.createLinearGradient(x, y, x + BAY_WIDTH, y + BAY_HEIGHT);
        grad.addColorStop(0, bay.occupied ? "hsl(45,95%,20%)" : "hsl(45,95%,25%)");
        grad.addColorStop(1, bay.occupied ? "hsl(45,95%,15%)" : "hsl(45,95%,20%)");
        ctx.fillStyle = grad;
      } else {
        ctx.fillStyle = bay.occupied ? "hsl(215,20%,22%)" : "hsl(215,20%,18%)";
      }

      ctx.fillRect(x, y, BAY_WIDTH, BAY_HEIGHT);

      ctx.strokeStyle = bay.isPremium
        ? bay.occupied ? "hsl(45,95%,40%)" : "hsl(45,95%,60%)"
        : bay.occupied ? "hsl(215,20%,30%)" : "hsl(215,20%,25%)";

      ctx.lineWidth = 2;
      ctx.strokeRect(x, y, BAY_WIDTH, BAY_HEIGHT);

      ctx.fillStyle = bay.isPremium ? "hsl(45,95%,70%)" : "hsl(210,20%,70%)";
      ctx.font = "bold 11px monospace";
      ctx.fillText(bay.isPremium ? `P${bay.id}` : `${bay.id}`, x + BAY_WIDTH / 2, y + 22);

      ctx.fillStyle = "hsl(210,15%,55%)";
      ctx.font = "8px monospace";
      ctx.fillText(bay.isPremium ? "SHORT" : "LONG", x + BAY_WIDTH / 2, y + 35);
    });

    // Paths
    cars.forEach((car) => {
      if (car.status !== "parked") {
        const color =
          car.duration === "short"
            ? "hsl(45,95%,60%)"
            : "hsl(210,100%,65%)";

        ctx.strokeStyle = color.replace(")", " / 0.2)");
        ctx.lineWidth = 2;
        ctx.setLineDash([8, 4]);
        ctx.beginPath();
        ctx.moveTo(car.path[0].x, car.path[0].y);
        car.path.forEach((p) => ctx.lineTo(p.x, p.y));
        ctx.stroke();
        ctx.setLineDash([]);
      }
    });

    // Cars
    cars.forEach((car) => {
      ctx.save();
      ctx.translate(car.position.x, car.position.y);

      const next = car.path[car.pathIndex];
      let angle = next ? Math.atan2(next.y - car.position.y, next.x - car.position.x) : 0;
      ctx.rotate(angle);

      const color =
        car.duration === "short" ? "hsl(45,95%,60%)" : "hsl(210,100%,65%)";

      ctx.shadowColor = color;
      ctx.shadowBlur = 15;

      ctx.fillStyle = color;
      ctx.fillRect(-15, -9, 30, 18);

      ctx.shadowBlur = 0;

      ctx.restore();
    });
  }, [cars, bays, width, height]);

  return (
    <canvas
      ref={canvasRef}
      width={width}
      height={height}
      style={{
        borderRadius: "0.75rem",
        border: "2px solid hsl(215,20%,20%)",
        width: "100%",
        display: "block",
      }}
    />
  );
}

// ————————————————————— Control Panel —————————————————————
function ControlPanel({
  isRunning,
  speed,
  onToggle,
  onReset,
  onAddCar,
  onSpeedChange,
  onToggleMusic,
  musicOn,
}) {
  return (
    <div
      style={{
        marginTop: "0.75rem",
        padding: "0.75rem",
        borderRadius: "0.75rem",
        border: "1px solid hsl(215,20%,20%)",
        background: "hsl(215,25%,12%)",
        display: "flex",
        flexDirection: "column",
        gap: "0.75rem",
      }}
    >
      <div style={{ display: "flex", gap: "0.5rem", flexWrap: "wrap" }}>
        <button onClick={onToggle} style={btn(isRunning ? "#ff4242" : "#4da6ff")}>
          {isRunning ? "⏸ Pause" : "▶ Start"}
        </button>

        <button onClick={onAddCar} style={btn("#2a3245")}>
          ➕ Add Car
        </button>

        <button onClick={onToggleMusic} style={btn("#2a3245")}>
          {musicOn ? "🎵 Music" : "🔇 Music Off"}
        </button>

        <button onClick={onReset} style={outlineBtn()}>
          🔁 Reset
        </button>
      </div>

      <div style={{ display: "flex", alignItems: "center", gap: "0.5rem" }}>
        <span style={{ fontSize: "0.8rem", color: "hsl(210,15%,65%)" }}>
          Speed:
        </span>

        <input
          type="range"
          min="0.5"
          max="3"
          step="0.5"
          value={speed}
          onChange={(e) => onSpeedChange(parseFloat(e.target.value))}
          style={{ flex: 1 }}
        />

        <span style={{ fontSize: "0.8rem", color: "#fff" }}>
          {speed.toFixed(1)}x
        </span>
      </div>
    </div>
  );
}

function btn(bg) {
  return {
    padding: "0.4rem 0.75rem",
    borderRadius: "999px",
    fontSize: "0.85rem",
    fontWeight: 600,
    background: bg,
    color: "white",
    border: "none",
    cursor: "pointer",
  };
}

function outlineBtn() {
  return {
    padding: "0.4rem 0.75rem",
    borderRadius: "999px",
    fontSize: "0.85rem",
    fontWeight: 600,
    background: "transparent",
    color: "white",
    border: "1px solid hsl(215,20%,25%)",
    cursor: "pointer",
  };
}

// ————————————————————— Main Component —————————————————————
export default function WParkTrafficDemo({ autoplayMusic }) {
  const [bays, setBays] = useState([]);
  const [cars, setCars] = useState([]);
  const [isRunning, setIsRunning] = useState(true);
  const [speed, setSpeed] = useState(1);
  const [carIdCounter, setCarIdCounter] = useState(1);

  // ===== AUDIO =====
  const audioRef = useRef(null);
  const [musicOn, setMusicOn] = useState(false);

  // Create audio ONCE from public folder
  useEffect(() => {
    const audio = new Audio(SOUNDTRACK_SRC);
    audio.loop = true;
    audio.volume = 0.55;

    audio.onplay = () => setMusicOn(true);
    audio.onpause = () => setMusicOn(false);

    audioRef.current = audio;

    return () => {
      audio.pause();
      audio.currentTime = 0;
    };
  }, []);

  // Autoplay when triggered
  useEffect(() => {
    if (autoplayMusic && audioRef.current) {
      audioRef.current.play().catch(() => {});
    }
  }, [autoplayMusic]);

  const toggleMusic = () => {
    if (!audioRef.current) return;

    if (musicOn) audioRef.current.pause();
    else audioRef.current.play().catch(() => {});
  };

  // ——— INIT BAYS ———
  useEffect(() => {
    const initial = [];
    const spacing = 20;
    const startX = 150;
    const topY = 120;
    const bottomY = 420;
    const entryX = CANVAS_WIDTH / 2;

    for (let i = 0; i < 4; i++) {
      const x = startX + i * (BAY_WIDTH + spacing);
      const center = x + BAY_WIDTH / 2;
      const dist = Math.abs(center - entryX);

      initial.push({
        id: i + 1,
        position: { x, y: topY },
        occupied: false,
        width: BAY_WIDTH,
        height: BAY_HEIGHT,
        isPremium: dist < 150,
        distanceToExit: dist,
      });

      initial.push({
        id: i + 5,
        position: { x, y: bottomY },
        occupied: false,
        width: BAY_WIDTH,
        height: BAY_HEIGHT,
        isPremium: dist < 150,
        distanceToExit: dist,
      });
    }

    setBays(initial);
  }, []);

  // ——— BAY SELECTION ———
  const findOptimalBay = useCallback(
    (duration) => {
      const available = bays.filter((b) => !b.occupied);
      if (!available.length) return null;

      if (duration === "short") {
        const premium = available.filter((b) => b.isPremium);
        if (premium.length) {
          return premium.sort((a, b) => a.distanceToExit - b.distanceToExit)[0];
        }
      } else {
        const standard = available.filter((b) => !b.isPremium);
        if (standard.length) {
          return standard.sort((a, b) => b.distanceToExit - a.distanceToExit)[0];
        }
      }

      return available[0];
    },
    [bays]
  );

  // ——— ADD CAR ———
  const addCar = useCallback(
    (duration) => {
      const d = duration || (Math.random() > 0.5 ? "short" : "long");
      const bay = findOptimalBay(d);
      if (!bay) return;

      const entry = { x: CANVAS_WIDTH / 2, y: 60 };
      const target = {
        x: bay.position.x + BAY_WIDTH / 2,
        y: bay.position.y + BAY_HEIGHT / 2,
      };

      const newCar = {
        id: carIdCounter,
        position: { ...entry },
        targetBay: bay.id,
        path: calculatePath(entry, target),
        pathIndex: 0,
        status: "entering",
        speed: 2,
        duration: d,
      };

      setCars((prev) => [...prev, newCar]);
      setCarIdCounter((p) => p + 1);

      setBays((prev) =>
        prev.map((b) => (b.id === bay.id ? { ...b, occupied: true } : b))
      );
    },
    [carIdCounter, findOptimalBay]
  );

  // ——— UPDATE CARS ———
  const updateCarPositions = useCallback(() => {
    setCars((prev) =>
      prev.map((car) => {
        if (car.status === "parked" || car.pathIndex >= car.path.length) {
          return { ...car, status: "parked" };
        }

        const next = car.path[car.pathIndex];
        const dist = distance(car.position, next);

        if (dist < 5) {
          return {
            ...car,
            pathIndex: car.pathIndex + 1,
            status: car.pathIndex === car.path.length - 2 ? "parking" : car.status,
          };
        }

        const angle = Math.atan2(next.y - car.position.y, next.x - car.position.x);
        const move = car.speed * speed;

        return {
          ...car,
          position: {
            x: car.position.x + Math.cos(angle) * move,
            y: car.position.y + Math.sin(angle) * move,
          },
          status: "moving",
        };
      })
    );
  }, [speed]);

  useEffect(() => {
    if (!isRunning) return;
    const interval = setInterval(updateCarPositions, 1000 / 60);
    return () => clearInterval(interval);
  }, [isRunning, updateCarPositions]);

  // AUTO ADD
  useEffect(() => {
    if (!isRunning) return;
    const interval = setInterval(() => {
      const anyAvailable = bays.some((b) => !b.occupied);
      const allParked = cars.every((c) => c.status === "parked");

      if (cars.length < 6) {
        addCar();
      } else if (!anyAvailable && allParked) {
        setCars([]);
        setCarIdCounter(1);
        setBays((prev) => prev.map((b) => ({ ...b, occupied: false })));
      }
    }, 3000);

    return () => clearInterval(interval);
  }, [isRunning, cars, bays, addCar]);

  const resetAll = () => {
    setCars([]);
    setCarIdCounter(1);
    setBays((prev) => prev.map((b) => ({ ...b, occupied: false })));
    setIsRunning(false);
  };

  // ————————————————————— RENDER —————————————————————
  return (
    <div
      style={{
        width: "100%",
        maxWidth: "1000px",
        margin: "0 auto",
        display: "flex",
        flexDirection: "column",
      }}
    >
      <div
        style={{
          borderRadius: "1.25rem",
          padding: "0.75rem",
          background: "radial-gradient(circle at top, #020617, #020617)",
          border: "1px solid hsl(215,20%,20%)",
        }}
      >
        <TrafficCanvas
          cars={cars}
          bays={bays}
          width={CANVAS_WIDTH}
          height={CANVAS_HEIGHT}
        />
      </div>

      <ControlPanel
        isRunning={isRunning}
        speed={speed}
        onToggle={() => setIsRunning((prev) => !prev)}
        onReset={resetAll}
        onAddCar={() => addCar()}
        onSpeedChange={setSpeed}
        onToggleMusic={toggleMusic}
        musicOn={musicOn}
      />
    </div>
  );
}
